/*
 * sp-videos.js — player del popup de la sección de videos (CONTRACTS §C.2, §K.3-K.4; PARITY PAR-44, 65, 120, 131).
 *
 * window.spVideos = { init(root), destroy(root), get(root) } — una instancia por raíz .sp-videos (WeakMap), con
 * auto-init y soporte del editor de temas (shopify:section:load/unload). El marquee lo mueve sp-marquee.js.
 *
 * - El único elemento de video de la sección vive dentro del modal y arranca sin src: recibe el src (data-video-src
 *   de la card) recién al abrir, se reproduce y, al cerrar, se pausa y se le saca el src (load() corta la descarga y
 *   libera memoria). Cero bytes de mp4 en la carga de la página.
 * - Modal propio de la sección: [data-video-modal="<id de la sección>"] dentro de la raíz (con dos instancias de la
 *   sección cada una abre el suyo).
 * - Mientras el modal está abierto la raíz lleva is-open: el motor pausa la marcha. Cierre: botón ×, click fuera del
 *   video (backdrop) y Esc nativo.
 * - En los dos modos (PAR-131, robustez de la referencia b69ca1b): un solo video a la vez (abrir uno cierra cualquier
 *   otro modal de videos), el scroll de la página queda quieto mientras el modal está abierto sin correr nada de lugar
 *   (html.sp-videos-lock: overflow hidden y, si desaparece una barra con ancho, ese ancho como padding), al cerrar el foco vuelve adonde
 *   estaba, si el navegador no deja arrancar con sonido arranca sin sonido y, sin <dialog> (iOS < 15.4, algunas apps),
 *   el video se abre solo, a pantalla completa.
 * - Reproductor propio (modal con data-sp-player=custom: setting custom_player, brief socialProof.videos.player custom;
 *   port de la referencia, commits d43253e y 57f1836): sin controls del sistema (en iPhone se encimaban con la X).
 *   Tocar el video pausa o reanuda (en pausa, el play grande); el póster de la card mientras carga y una ruedita si
 *   tarda más de 0,3 s (CSS); barra de avance por rAF sólo con el modal abierto y el video andando; loop. Arranca con
 *   sonido (el toque en la card lo habilita); si el navegador no deja, arranca sin sonido y el botón queda en silencio;
 *   si la persona silencia, los siguientes abren sin sonido. El botón de sonido aparece sólo si el video tiene audio
 *   (audioTracks en Safari, mozHasAudio en Firefox, webkitAudioDecodedByteCount en Chrome): los videos que arma el kit
 *   salen sin audio (PAR-46). Teclado: el foco va al modal (sin anillo sobre el video), Tab recorre sólo los botones
 *   del modal, espacio o K pausa, M silencia, Esc cierra. Sin listeners de gesto ni de scroll.
 * - Música de fondo del reproductor propio (modal con data-music-src: setting music_asset, brief socialProof.videos.music,
 *   2026-10-17): el elemento de audio NO existe hasta que se abre el primer video (cero bytes en la carga): ahí se crea con
 *   preload none, loop y volumen 0,5, recibe el src y arranca (el toque en la card es el gesto que lo permite). Suena
 *   mientras el modal está abierto (tocar el video para pausarlo también la pausa; si el navegador no la deja arrancar, el
 *   botón se ve silenciado y un toque la arranca), se pausa al cerrarlo (sigue desde donde quedó en la próxima apertura) y al pasar la
 *   pestaña a segundo plano (visibilitychange). Con música el botón de sonido aparece y la silencia o la vuelve a poner; la
 *   elección se recuerda entre aperturas. Un video que trae su propio audio (detectAudio) usa el suyo: con ése la música no
 *   suena y el botón vuelve a manejar el sonido del video, como siempre. Mientras suena la música el video va silenciado. En
 *   iOS el volumen de un elemento de audio es de sólo lectura (lo maneja el sistema): ahí la música suena al volumen del
 *   teléfono; confirmalo en un iPhone real (el arnés corre en Chromium).
 */
(function () {
  'use strict';
  if (window.spVideos && window.spVideos.__kit) return;

  var ROOT = '.sp-videos[data-section-id]';
  var instances = new WeakMap();
  var locks = 0;

  function closest(el, sel) {
    return el && el.closest ? el.closest(sel) : null;
  }
  function attrSelector(name, value) {
    // comillas como \u0022: así ningún escáner de strings (tests/social: textos visibles) ve una comilla suelta
    var v = window.CSS && window.CSS.escape ? window.CSS.escape(value) : String(value).replace(/[\u0022\\]/g, '\\$&');
    return '[' + name + '=\u0022' + v + '\u0022]';
  }
  function outside(dlg, e) {
    if (e.target !== dlg) return false;
    if (!e.clientX && !e.clientY) return false;
    var r = dlg.getBoundingClientRect();
    return e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
  }

  // scroll de la página quieto mientras haya un modal de videos abierto. Si al sacar el scroll desaparece una barra que
  // ocupaba lugar (Windows; en el celular y en macOS flotan y miden 0), html recibe ese ancho como padding-right para que
  // nada se corra (Chromium ignora scrollbar-gutter en la raíz con overflow hidden: se mide lo que creció)
  function lockScroll() {
    var html = document.documentElement;
    locks++;
    if (locks > 1) return;
    var before = html.clientWidth;
    html.classList.add('sp-videos-lock');
    var grow = html.clientWidth - before;
    if (grow > 0) {
      html.style.setProperty('--sp-videos-gutter', grow + 'px');
      html.classList.add('sp-videos-lock--gutter');
    }
  }
  function unlockScroll() {
    if (!locks) return;
    locks--;
    if (locks) return;
    var html = document.documentElement;
    html.classList.remove('sp-videos-lock');
    html.classList.remove('sp-videos-lock--gutter');
    html.style.removeProperty('--sp-videos-gutter');
  }

  // un solo video a la vez: cualquier otro modal de videos abierto se cierra (su 'close' le saca el src)
  function closeOthers(modal) {
    var open = document.querySelectorAll('dialog.sp-videos__modal[open]');
    for (var i = 0; i < open.length; i++) {
      if (open[i] === modal) continue;
      var v = open[i].querySelector('video');
      if (v) { try { v.pause(); } catch (err) { /* nada */ } }
      if (typeof open[i].close === 'function') open[i].close();
    }
  }

  function Videos(root) {
    this.root = root;
    var id = root.getAttribute('data-section-id') || '';
    this.modal = (id && root.querySelector(attrSelector('data-video-modal', id))) || null;
    this.player = this.modal ? this.modal.querySelector('.sp-videos__player') : null;
    this.closeBtn = this.modal ? this.modal.querySelector('[data-video-close]') : null;
    this.toggle = null;
    this.sound = null;
    this.bar = null;
    if (this.modal && this.modal.getAttribute('data-sp-player') === 'custom') {
      this.toggle = this.modal.querySelector('[data-video-toggle]');
      this.sound = this.modal.querySelector('[data-video-sound]');
      this.bar = this.modal.querySelector('[data-video-progress]');
    }
    this.custom = !!(this.toggle && this.sound);
    // música de fondo (sólo con el reproductor propio): la URL y, recién al abrir, el elemento de audio
    this.musicSrc = this.custom ? this.modal.getAttribute('data-music-src') || '' : '';
    this.music = null;
    this.musicMuted = false; // lo que eligió la persona con el botón de sonido (con música)
    this.musicBlocked = false; // el navegador no dejó arrancar la música: el botón se ve silenciado y un toque la arranca
    this.userPaused = false; // la persona pausó el video tocándolo: la música también se pausa
    this.bound = [];
    this.loading = false;
    this.wantMuted = false; // lo que eligió la persona con el botón de sonido
    this.audio = {}; // src → true cuando se detectó audio
    this.rafId = 0;
    this.locked = false;
    this.returnFocus = null;
    this._tick = this.tick.bind(this);
  }

  Videos.prototype.on = function (target, type, fn, options) {
    if (!target) return;
    target.addEventListener(type, fn, options);
    this.bound.push([target, type, fn, options]);
  };

  Videos.prototype.start = function () {
    var self = this;
    var modal = this.modal;
    if (!modal || !this.player) return;
    this.on(this.root, 'click', function (e) {
      var card = closest(e.target, '.sp-videos__card[data-video-src]');
      if (!card || !self.root.contains(card)) return;
      self.open(card.getAttribute('data-video-src'), card);
    });
    if (typeof modal.showModal !== 'function') return; // sin <dialog>: open() abre el video solo
    this.on(modal, 'click', function (e) {
      if (closest(e.target, '[data-video-close]') || outside(modal, e)) { modal.close(); return; }
      if (!self.custom) return;
      if (closest(e.target, '[data-video-sound]')) self.toggleSound();
      else if (closest(e.target, '[data-video-toggle]')) self.togglePlay();
    });
    this.on(modal, 'close', function () { self.onClose(); });
    if (this.custom) this.startCustom();
  };

  Videos.prototype.startCustom = function () {
    var self = this;
    var p = this.player;
    var paint = function () { self.paint(); };
    var idle = function () { self.loading = false; self.paint(); };
    var audio = function () { self.detectAudio(); };
    this.on(p, 'play', paint);
    this.on(p, 'volumechange', paint);
    this.on(p, 'pause', idle);
    this.on(p, 'error', idle);
    this.on(p, 'waiting', function () { self.loading = true; self.paint(); });
    this.on(p, 'playing', function () { self.loading = false; self.paint(); self.kick(); self.detectAudio(); });
    this.on(p, 'loadedmetadata', audio);
    this.on(p, 'timeupdate', audio);
    this.on(this.modal, 'keydown', function (e) { self.onKey(e); });
    if (this.musicSrc) this.on(document, 'visibilitychange', function () { self.syncMusic(); });
  };

  /** ¿Suena la música con el video de ahora? Con música, salvo que el video traiga su propio audio. */
  Videos.prototype.musicMode = function () {
    if (!this.musicSrc) return false;
    var src = this.player.getAttribute('src');
    return !(src && this.audio[src]);
  };

  /**
   * Pone o pausa la música según el estado: suena con el modal abierto, la pestaña visible, sin silenciar y con un video sin
   * audio propio. El elemento se crea la primera vez que tiene que sonar (open() corre dentro del toque en la card).
   */
  Videos.prototype.syncMusic = function () {
    if (!this.musicSrc) return;
    var want = !!this.modal.open && this.musicMode() && !this.musicMuted && !this.userPaused && document.visibilityState !== 'hidden';
    if (!want) {
      if (this.music && !this.music.paused) { try { this.music.pause(); } catch (err) { /* nada */ } }
      return;
    }
    if (!this.music) {
      var a = document.createElement('audio');
      a.preload = 'none';
      a.loop = true;
      a.volume = 0.5;
      a.setAttribute('data-sp-videos-music', '');
      a.src = this.musicSrc;
      this.music = a;
    }
    if (!this.music.paused) return;
    var self = this;
    var p;
    try { p = this.music.play(); } catch (err) { p = null; }
    if (p && typeof p.then === 'function') {
      p.then(function () { self.musicBlocked = false; self.paint(); }, function () { self.musicBlocked = true; self.paint(); });
    }
  };

  Videos.prototype.open = function (src, card) {
    if (!src) return;
    var modal = this.modal;
    var player = this.player;
    if (typeof modal.showModal !== 'function') {
      window.location.href = src; // navegador sin <dialog>: el video se abre solo, a pantalla completa
      return;
    }
    closeOthers(modal);
    if (player.getAttribute('src') !== src) {
      if (this.custom) {
        // mientras carga se ve el póster que ya mostró la card (está en caché)
        var img = card && card.querySelector ? card.querySelector('img') : null;
        // con la foto diferida todavía sin soltar (PAR-135), la URL real de data-sp-src y no el SVG vacío
        var poster = img ? img.getAttribute('data-sp-src') || img.currentSrc || img.getAttribute('src') || '' : '';
        if (poster) player.setAttribute('poster', poster);
        else player.removeAttribute('poster');
        if (this.bar) this.bar.style.transform = 'scaleX(0)';
        // el botón de sonido: con audio en el video o con música de fondo
        this.sound.hidden = this.audio[src] !== true && !this.musicSrc;
      }
      player.setAttribute('src', src);
    }
    var active = document.activeElement;
    this.returnFocus = active && active !== document.body && !modal.contains(active) ? active : null;
    this.root.classList.add('is-open');
    try {
      if (!modal.open) modal.showModal();
    } catch (err) {
      this.root.classList.remove('is-open');
      window.location.href = src;
      return;
    }
    this.lock();
    if (this.custom) {
      // el foco queda en el modal y no en un botón: si no, Safari dibuja un borde sobre el video
      try { modal.focus({ preventScroll: true }); } catch (err) { /* nada */ }
      // con música, el video arranca silenciado (los del kit no traen audio): así en iOS los dos no se disputan el sonido
      player.muted = this.musicMode() ? true : this.wantMuted;
      this.userPaused = false;
      this.loading = true;
      this.paint();
    }
    this.play();
    if (this.custom) { this.syncMusic(); this.paint(); }
  };

  Videos.prototype.play = function () {
    var self = this;
    var player = this.player;
    var p;
    try { p = player.play(); } catch (err) { p = null; }
    if (!p || typeof p.catch !== 'function') return;
    p.catch(function (err) {
      if (!self.modal.open) return; // se cerró antes de arrancar (al sacar el src, play() se rechaza)
      if (err && err.name === 'NotAllowedError' && !player.muted) {
        // el navegador no deja arrancar con sonido: arranca sin sonido (el botón de sonido o los controles quedan a mano)
        player.muted = true;
        var again;
        try { again = player.play(); } catch (e2) { again = null; }
        if (again && typeof again.catch === 'function') again.catch(function () { self.loading = false; self.paint(); });
        return;
      }
      // no arrancó: queda el play grande en el medio (o los controles nativos) para tocarlo
      self.loading = false;
      self.paint();
    });
  };

  Videos.prototype.togglePlay = function () {
    // tocar el video pausa o reanuda también la música (dentro del gesto: el play() de la música está permitido)
    if (this.player.paused) { this.userPaused = false; this.play(); } else { this.userPaused = true; this.player.pause(); }
    this.syncMusic();
  };

  Videos.prototype.toggleSound = function () {
    if (this.musicMode()) {
      // con música, el botón la silencia o la vuelve a poner (y se recuerda en las próximas aperturas)
      // si el navegador no la dejó arrancar, el primer toque la arranca (el botón ya se veía silenciado)
      if (this.musicBlocked) { this.musicBlocked = false; this.musicMuted = false; } else this.musicMuted = !this.musicMuted;
      this.syncMusic();
      this.paint();
      return;
    }
    this.wantMuted = !this.player.muted;
    this.player.muted = this.wantMuted;
  };

  /** Reproductor propio: clases de estado del modal y nombres accesibles de los botones (data-label-* de la sección). */
  Videos.prototype.paint = function () {
    if (!this.custom) return;
    var p = this.player;
    var m = this.modal;
    var paused = p.paused;
    m.classList.toggle('is-loading', this.loading);
    m.classList.toggle('is-paused', paused && !this.loading);
    this.toggle.setAttribute('aria-label', m.getAttribute(paused ? 'data-label-play' : 'data-label-pause') || '');
    var muted = this.musicMode() ? this.musicMuted || this.musicBlocked : p.muted;
    this.sound.classList.toggle('is-muted', muted);
    this.sound.setAttribute('aria-label', m.getAttribute(muted ? 'data-label-unmute' : 'data-label-mute') || '');
  };

  /** ¿El video tiene audio? Sólo con evidencia se muestra el botón de sonido (sin ninguna API para saberlo, queda). */
  Videos.prototype.detectAudio = function () {
    var p = this.player;
    var src = p.getAttribute('src');
    if (!src || this.audio[src]) return;
    var tracks = p.audioTracks;
    var decoded = p.webkitAudioDecodedByteCount;
    var moz = p.mozHasAudio;
    var has = false;
    if (typeof moz === 'boolean') has = moz;
    else if (tracks && tracks.length > 0) has = true;
    else if (typeof decoded === 'number' && decoded > 0) has = true;
    else if (!tracks && typeof decoded !== 'number') has = true;
    if (!has) return;
    this.audio[src] = true;
    this.sound.hidden = false;
    if (this.musicSrc && this.modal.open) this.player.muted = this.wantMuted; // su audio, con la elección de siempre
    // un video con su propio audio: la música no suena con él y el botón vuelve a manejar el video
    this.syncMusic();
    this.paint();
  };

  Videos.prototype.kick = function () {
    if (!this.rafId && this.bar) this.rafId = requestAnimationFrame(this._tick);
  };

  Videos.prototype.tick = function () {
    this.rafId = 0;
    var p = this.player;
    if (p.duration && isFinite(p.duration)) this.bar.style.transform = 'scaleX(' + Math.min(1, p.currentTime / p.duration).toFixed(4) + ')';
    if (this.modal.open && !p.paused) this.rafId = requestAnimationFrame(this._tick);
  };

  /** Teclado del reproductor propio: Tab sólo por los botones del modal; espacio o K pausa, M silencia (sobre el modal). */
  Videos.prototype.onKey = function (e) {
    var modal = this.modal;
    if (e.key === 'Tab') {
      var f = this.focusables();
      if (!f.length) return;
      var a = document.activeElement;
      var first = f[0];
      var last = f[f.length - 1];
      var inside = modal.contains(a);
      if (e.shiftKey ? a === first || a === modal || !inside : a === last || !inside) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      }
      return;
    }
    if (e.target !== modal || e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.key === ' ' || e.key === 'Spacebar' || e.key === 'k' || e.key === 'K') {
      e.preventDefault();
      this.togglePlay();
    } else if ((e.key === 'm' || e.key === 'M') && !this.sound.hidden) {
      this.toggleSound();
    }
  };

  Videos.prototype.focusables = function () {
    var out = [];
    var list = [this.toggle, this.sound, this.closeBtn];
    for (var i = 0; i < list.length; i++) if (list[i] && !list[i].hidden && !list[i].disabled) out.push(list[i]);
    return out;
  };

  Videos.prototype.lock = function () {
    if (this.locked) return;
    this.locked = true;
    lockScroll();
  };

  Videos.prototype.unlock = function () {
    if (!this.locked) return;
    this.locked = false;
    unlockScroll();
  };

  Videos.prototype.onClose = function () {
    this.stopPlayer();
    this.unlock();
    var r = this.returnFocus;
    this.returnFocus = null;
    if (!r || !r.isConnected || typeof r.focus !== 'function') return;
    // el <dialog> ya devuelve el foco en los navegadores nuevos; si no lo hizo (queda en el body o en el modal), acá
    var a = document.activeElement;
    if (!a || a === document.body || a === this.modal || this.modal.contains(a)) {
      try { r.focus({ preventScroll: true }); } catch (err) { /* nada */ }
    }
  };

  Videos.prototype.stopPlayer = function () {
    var player = this.player;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    if (player) {
      try { player.pause(); } catch (err) { /* nada */ }
      if (player.hasAttribute('src')) {
        player.removeAttribute('src');
        try { player.load(); } catch (err) { /* nada */ }
      }
    }
    this.loading = false;
    this.syncMusic(); // con el modal cerrado: pausa la música (queda en su posición)
    this.paint();
    this.root.classList.remove('is-open');
  };

  Videos.prototype.destroy = function () {
    if (this.modal && this.modal.open && this.modal.close) this.modal.close();
    this.stopPlayer();
    this.unlock();
    this.returnFocus = null;
    if (this.music) {
      try { this.music.pause(); } catch (err) { /* nada */ }
      this.music.removeAttribute('src');
      try { this.music.load(); } catch (err) { /* nada */ }
      this.music = null;
    }
    for (var i = 0; i < this.bound.length; i++) {
      var b = this.bound[i];
      b[0].removeEventListener(b[1], b[2], b[3]);
    }
    this.bound.length = 0;
    instances.delete(this.root);
  };

  function rootsIn(node) {
    var out = [];
    if (!node || node.nodeType !== 1) return out;
    if (node.matches(ROOT)) out.push(node);
    var found = node.querySelectorAll(ROOT);
    for (var i = 0; i < found.length; i++) out.push(found[i]);
    return out;
  }

  var api = {
    __kit: 1,
    init: function (root) {
      if (!root || root.nodeType !== 1) return null;
      var inst = instances.get(root);
      if (inst) return inst;
      inst = new Videos(root);
      instances.set(root, inst);
      inst.start();
      return inst;
    },
    destroy: function (root) {
      var inst = root ? instances.get(root) : undefined;
      if (!inst) return false;
      inst.destroy();
      return true;
    },
    get: function (root) {
      return root ? instances.get(root) : undefined;
    },
    initAll: function (scope) {
      var roots = rootsIn(scope && scope.nodeType === 1 ? scope : document.documentElement);
      for (var i = 0; i < roots.length; i++) api.init(roots[i]);
    },
    destroyAll: function (scope) {
      var roots = rootsIn(scope && scope.nodeType === 1 ? scope : document.documentElement);
      for (var i = 0; i < roots.length; i++) api.destroy(roots[i]);
    }
  };
  window.spVideos = api;

  document.addEventListener('shopify:section:unload', function (e) { api.destroyAll(e.target); });
  document.addEventListener('shopify:section:load', function (e) { api.initAll(e.target); });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { api.initAll(); });
  else api.initAll();
})();
